import { Scene, GameObjects } from 'phaser';
import * as Phaser from 'phaser';
import UIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import { audioManager } from '../core/CoreManagerAudio';

interface LevelWinConfig {
  title?: string;
  subtitle?: string;
  continueText?: string;
  replayText?: string;
}

export class LevelWin extends Scene {
  override rexUI!: UIPlugin;
  private levelId: string = 'level1';
  private levelNumber: number = 1;
  private config: LevelWinConfig = {};

  constructor() {
    super('LevelWin');
  }

  create(data?: { levelId?: string; biomatter?: number; config?: LevelWinConfig }) {
    this.rexUI = (this as unknown as { rexUI: UIPlugin }).rexUI;
    this.levelId = data?.levelId ?? 'level1';
    this.levelNumber = this.parseLevelNumber(this.levelId);
    this.config = data?.config ?? {};

    this.cameras.main.setBackgroundColor(0x0b1622);
    this.add.rectangle(this.scale.width / 2, this.scale.height / 2, this.scale.width, this.scale.height, 0x000000, 0.55);

    const title = this.add.text(this.scale.width / 2, 220, this.config.title ?? 'MISSION COMPLETE', {
      fontFamily: 'Arial Black',
      fontSize: '64px',
      color: '#66ff99',
      stroke: '#000000',
      strokeThickness: 8,
      align: 'center',
    }).setOrigin(0.5);

    const subtitle = this.add.text(this.scale.width / 2, 300, this.config.subtitle ?? `Level ${this.levelNumber} cleared`, {
      fontFamily: 'Arial',
      fontSize: '30px',
      color: '#ffffff',
      align: 'center',
    }).setOrigin(0.5);

    const continueBtn = this.createButton(this.config.continueText ?? 'Continue', 0x3d8a56, async () => {
      this.scene.start('LevelSelect');
    });
    continueBtn.setPosition(this.scale.width / 2 - 130, 440);

    const replayBtn = this.createButton(this.config.replayText ?? 'Replay', 0x3b5a88, () => {
      this.scene.start('LevelPreloader', { levelId: this.levelId, levelNum: this.levelNumber });
    });
    replayBtn.setPosition(this.scale.width / 2 + 130, 440);

    this.tweens.add({
      targets: [title, subtitle, continueBtn, replayBtn],
      alpha: { from: 0, to: 1 },
      duration: 350,
      ease: 'Power2',
    });

    audioManager.stopMusic();
    audioManager.playUISound('click', this);
  }

  private parseLevelNumber(levelId: string): number {
    const n = parseInt(levelId.replace('level', ''), 10);
    return Number.isNaN(n) ? 1 : n;
  }

  private createButton(text: string, bgColor: number, onClick: () => void): GameObjects.Container {
    const container = this.add.container(0, 0);
    const bg = this.rexUI.add.roundRectangle(0, 0, 220, 64, 12, bgColor).setStrokeStyle(3, 0xffffff);
    const label = this.add.text(0, 0, text, {
      fontFamily: 'Arial Black',
      fontSize: '24px',
      color: '#ffffff',
    }).setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(220, 64);
    container.setInteractive(new Phaser.Geom.Rectangle(-110, -32, 220, 64), Phaser.Geom.Rectangle.Contains);

    const normalColor = bgColor;
    const hoverColor = Phaser.Display.Color.ValueToColor(bgColor).lighten(20).color;
    const downColor = Phaser.Display.Color.ValueToColor(bgColor).darken(20).color;

    container.on('pointerover', () => {
      bg.setFillStyle(hoverColor);
      container.setScale(1.05);
      audioManager.playUISound('hover', this);
    });
    container.on('pointerout', () => {
      bg.setFillStyle(normalColor);
      container.setScale(1);
    });
    container.on('pointerdown', () => {
      bg.setFillStyle(downColor);
      container.setScale(0.95);
    });
    container.on('pointerup', () => {
      bg.setFillStyle(normalColor);
      container.setScale(1);
      audioManager.playUISound('click', this);
      onClick();
    });

    return container;
  }
}
